import { MandatoryChildren } from '../../../models/children-props';
import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  InstagramApiType,
  PinterestSearchType,
  SocialProfileFieldsFragment,
  ThirdPartyPost,
  ThirdPartyProfile
} from '../../../models/graphql';
import { PostSourceType } from '../../../constants/find-content';

import { fetchFacebookParameters } from './ContentFilterFacebook/ContentFilterFacebook.apollo';
import { fetchInstagramParameters } from './ContentFilterInstagram/ContentFilterInstagram.apollo';
import { SearchPinterestPostVariables } from './ContentFilterPinterest/ContentFilterPinterest.apollo';
import { SearchLinkedInPostsVariables } from './ContentFilterLinkedIn/ContentFilterLinkedIn.apollo';
import { ProfileMetrics } from 'constants/profileMetrics';

export type ProfileFindContent = SocialProfileFieldsFragment | ThirdPartyProfile | null | undefined;

export enum SearchStepEnum {
  ONE = 1,
  TWO = 2
}

export type FindContentValueType = {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loadingMore: boolean;
  setLoadingMore: (loading: boolean) => void;
  posts: ThirdPartyPost[];
  setPosts: Dispatch<SetStateAction<ThirdPartyPost[]>>;
  error: JSX.Element | string | undefined;
  setError: Dispatch<SetStateAction<JSX.Element | string | undefined>>;
  selectedPosts: Record<string, string>[];
  setSelectedPosts: Dispatch<SetStateAction<Record<string, string>[]>>;
  sourceType: PostSourceType | undefined;
  setSourceType: Dispatch<SetStateAction<PostSourceType | undefined>>;
  fetchMorePosts: (callBack: CallableFunction) => void;
  profiles: SocialProfileFieldsFragment[];
  gridRef: React.MutableRefObject<HTMLDivElement | null>;
  platform: string | undefined;
  platformType: string | undefined;
  searchStep: SearchStepEnum;
  setSearchStep: Dispatch<React.SetStateAction<SearchStepEnum>>;
  hasSearched: boolean;
  setHasSearched: (searched: boolean) => void;
  selectedProfile: ProfileFindContent | undefined;
  setSelectedProfile: Dispatch<SetStateAction<ProfileFindContent>>;
  selectedProfileSupportedMetrics: ProfileMetrics[];
  profileUsedToSearch: SocialProfileFieldsFragment | undefined;
  setProfileUsedToSearch: Dispatch<SetStateAction<SocialProfileFieldsFragment | undefined>>;
  onSearchResult: (
    parameters:
      | fetchFacebookParameters
      | fetchInstagramParameters
      | SearchPinterestPostVariables
      | SearchLinkedInPostsVariables,
    profiles: SocialProfileFieldsFragment[],
    isExternal: boolean,
    thirdPartyProfiles?: ThirdPartyProfile[]
  ) => void;
};

export type FindContentProviderProps = MandatoryChildren & {
  fetchMorePosts: (callBack: CallableFunction) => void;
  profiles: SocialProfileFieldsFragment[];
  platform: string | undefined;
  platformType: string | undefined;
};

export const FindContentContext = createContext<FindContentValueType>({
  loading: false,
  setLoading: () => {
    // ...
  },
  loadingMore: false,
  setLoadingMore: () => {
    // ...
  },
  posts: [],
  setPosts: () => {
    // ...
  },
  error: undefined,
  setError: () => {
    // ...
  },
  selectedPosts: [],
  setSelectedPosts: () => {
    // ...
  },
  sourceType: undefined,
  setSourceType: () => undefined,
  fetchMorePosts: () => {
    // ...
  },
  profiles: [],
  gridRef: {} as MutableRefObject<null>,
  platform: undefined,
  platformType: undefined,
  searchStep: SearchStepEnum.ONE,
  setSearchStep: () => {
    // ...
  },
  hasSearched: false,
  setHasSearched: () => {
    // ...
  },
  setSelectedProfile: () => {
    // ...
  },
  selectedProfile: undefined,

  selectedProfileSupportedMetrics: [],
  profileUsedToSearch: undefined,
  setProfileUsedToSearch: () => {
    // ...
  },
  onSearchResult: () => {
    // ...
  }
});

const profileMetricsMap: Record<string, ProfileMetrics[]> = {
  TwitterSocialProfile: [ProfileMetrics.shares, ProfileMetrics.likes],
  PinterestSocialProfile: [ProfileMetrics.comments],
  FacebookSocialProfile: [ProfileMetrics.likes, ProfileMetrics.comments, ProfileMetrics.shares],
  TikTokSocialProfile: [ProfileMetrics.likes, ProfileMetrics.comments, ProfileMetrics.videoViews, ProfileMetrics.shares]
};

const getMetricsForProfile = (
  selectedProfile: SocialProfileFieldsFragment,
  posts: ThirdPartyPost[]
): ProfileMetrics[] => {
  if (posts.length === 0) {
    return [];
  }
  if (
    selectedProfile?.__typename === 'InstagramSocialProfile' &&
    selectedProfile.apiType === InstagramApiType.BASIC_DISPLAY
  ) {
    return [];
  }
  if (!selectedProfile?.__typename) {
    return [ProfileMetrics.likes, ProfileMetrics.comments];
  }
  return profileMetricsMap[selectedProfile.__typename] || [ProfileMetrics.likes, ProfileMetrics.comments];
};

export const FindContentProvider = (props: FindContentProviderProps): JSX.Element => {
  const { children, fetchMorePosts, profiles, platform, platformType } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [sourceType, setSourceType] = useState<PostSourceType | undefined>(undefined);
  const [selectedProfile, setSelectedProfile] = useState<ProfileFindContent>(null);
  const [profileUsedToSearch, setProfileUsedToSearch] = useState<SocialProfileFieldsFragment>();
  const [posts, setPosts] = useState<ThirdPartyPost[]>([]);
  const [selectedPosts, setSelectedPosts] = useState<Record<string, string>[]>([]);
  const [error, setError] = useState<JSX.Element | string | undefined>(undefined);
  const [searchStep, setSearchStep] = useState<SearchStepEnum>(SearchStepEnum.ONE);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const gridRef = useRef<HTMLDivElement | null>(null);

  const selectedProfileSupportedMetrics = useMemo(() => {
    return profileUsedToSearch && platform ? getMetricsForProfile(profileUsedToSearch, posts) : [];
  }, [platform, profileUsedToSearch, posts]);

  const onSearchResult = useCallback(
    (
      parameters:
        | fetchFacebookParameters
        | fetchInstagramParameters
        | SearchPinterestPostVariables
        | SearchLinkedInPostsVariables,
      profiles: SocialProfileFieldsFragment[],
      isExternal: boolean,
      thirdPartyProfiles?: ThirdPartyProfile[]
    ) => {
      const matchedProfile = profiles.find(profile => profile.id === parameters.socialProfileId);
      if (parameters.searchType === PinterestSearchType.BOARD) {
        const externalProfile = profiles.find(
          p => p.__typename === 'PinterestSocialProfile' && p.boards.some(b => b.externalId === parameters.externalId)
        );
        setSelectedProfile(externalProfile);
      } else if (isExternal && thirdPartyProfiles) {
        const externalProfile = thirdPartyProfiles.find(profile => profile.externalId === parameters.externalId);
        setSelectedProfile(externalProfile);
      } else {
        setSelectedProfile(matchedProfile);
      }
      setProfileUsedToSearch(matchedProfile);
    },
    []
  );
  return (
    <FindContentContext.Provider
      value={{
        loading,
        setLoading,
        loadingMore,
        setLoadingMore,
        posts,
        setPosts,
        error,
        setError,
        selectedPosts,
        setSelectedPosts,
        sourceType,
        setSourceType,
        fetchMorePosts,
        profiles,
        gridRef,
        platform,
        platformType,
        searchStep,
        setSearchStep,
        hasSearched,
        setHasSearched,
        profileUsedToSearch,
        setProfileUsedToSearch,
        selectedProfile,
        setSelectedProfile,
        selectedProfileSupportedMetrics,
        onSearchResult
      }}>
      {children}
    </FindContentContext.Provider>
  );
};
